import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ArrowLeft, RefreshCw, Play, CheckCircle2, AlertTriangle, Eye } from "lucide-react";
import { toast } from "sonner";
import type { ManagerNotification, ManagerNotificationPair } from "@/hooks/useManagerNotifications";

type EstabLite = { id: string; name: string };

export default function AdminManagerNotifications() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<"active" | "dismissed" | "all">("active");
  const [estabFilter, setEstabFilter] = useState<string>("all");
  const [detail, setDetail] = useState<ManagerNotification | null>(null);
  const [running, setRunning] = useState(false);

  const { data: estabs = [] } = useQuery({
    queryKey: ["admin_estabs_for_notifs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("establishments")
        .select("id, name")
        .order("name");
      if (error) throw error;
      return (data ?? []) as EstabLite[];
    },
  });
  const estabMap = useMemo(() => {
    const m: Record<string, string> = {};
    for (const e of estabs) m[e.id] = e.name;
    return m;
  }, [estabs]);

  const { data: notifs = [], isLoading, refetch } = useQuery({
    queryKey: ["admin_manager_notifications", statusFilter, estabFilter],
    queryFn: async () => {
      let q = supabase
        .from("manager_notifications")
        .select("*")
        .order("created_at", { ascending: false });
      if (statusFilter === "active") q = q.is("dismissed_at", null);
      if (statusFilter === "dismissed") q = q.not("dismissed_at", "is", null);
      if (estabFilter !== "all") q = q.eq("establishment_id", estabFilter);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as ManagerNotification[];
    },
  });

  const runAudit = async () => {
    setRunning(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-collision-notifications", {
        method: "POST",
      });
      if (error) throw error;
      toast.success(
        `Audit terminé : ${data.scanned} résa(s) scannée(s), ${data.notifications_created} notif(s) créée(s), ${data.notifications_skipped_existing} ignorée(s) (déjà active), ${data.pairs_total} paire(s) au total.`,
      );
      qc.invalidateQueries({ queryKey: ["admin_manager_notifications"] });
      qc.invalidateQueries({ queryKey: ["manager_notifications"] });
    } catch (e) {
      toast.error("Échec de l'audit : " + (e as Error).message);
    } finally {
      setRunning(false);
    }
  };

  const dismissAsAdmin = async (id: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("manager_notifications")
      .update({ dismissed_at: new Date().toISOString(), dismissed_by: user?.id ?? null })
      .eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Notification marquée comme vue.");
    qc.invalidateQueries({ queryKey: ["admin_manager_notifications"] });
    qc.invalidateQueries({ queryKey: ["manager_notifications"] });
  };

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="bg-card border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <Link to="/admin">
              <Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-1" />Admin</Button>
            </Link>
            <h1 className="text-xl font-serif font-bold">Notifications managers</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="w-4 h-4 mr-1" />Rafraîchir
            </Button>
            <Button size="sm" onClick={runAudit} disabled={running}>
              <Play className="w-4 h-4 mr-1" />{running ? "Audit en cours…" : "Lancer un audit"}
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 space-y-4">
        <Card className="p-4 flex flex-wrap gap-3 items-center">
          <div className="text-sm font-medium">Filtres :</div>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Actives</SelectItem>
              <SelectItem value="dismissed">Vues (dismissed)</SelectItem>
              <SelectItem value="all">Toutes</SelectItem>
            </SelectContent>
          </Select>
          <Select value={estabFilter} onValueChange={setEstabFilter}>
            <SelectTrigger className="w-[260px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les établissements</SelectItem>
              {estabs.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="ml-auto text-sm text-muted-foreground">
            {notifs.length} résultat{notifs.length > 1 ? "s" : ""}
          </div>
        </Card>

        <Card className="overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">Chargement…</div>
          ) : notifs.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">Aucune notification.</div>
          ) : (
            <div className="divide-y">
              {notifs.map(n => {
                const pairs = (n.payload?.pairs ?? []) as ManagerNotificationPair[];
                return (
                  <div key={n.id} className="p-4 flex items-start gap-3">
                    {n.dismissed_at ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{estabMap[n.establishment_id] ?? n.establishment_id.slice(0, 8)}</span>
                        <Badge variant="outline" className="text-xs">{n.type}</Badge>
                        <Badge variant="outline" className="text-xs">{n.severity}</Badge>
                        {n.dismissed_at && <Badge variant="secondary" className="text-xs">Vue</Badge>}
                      </div>
                      <div className="text-sm mt-1">{n.title}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {pairs.length} paire{pairs.length > 1 ? "s" : ""} • Créé le {new Date(n.created_at).toLocaleString("fr-CH")}
                        {n.dismissed_at && ` • Vue le ${new Date(n.dismissed_at).toLocaleString("fr-CH")}`}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Button variant="outline" size="sm" onClick={() => setDetail(n)}>
                        <Eye className="w-4 h-4 mr-1" />Détails
                      </Button>
                      {!n.dismissed_at && (
                        <Button variant="ghost" size="sm" onClick={() => dismissAsAdmin(n.id)}>
                          Marquer vue
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </main>

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-xl">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle>{detail.title}</SheetTitle>
                <SheetDescription>
                  {estabMap[detail.establishment_id]} • {detail.severity}
                </SheetDescription>
              </SheetHeader>
              {detail.body && <p className="text-sm mt-4 whitespace-pre-wrap">{detail.body}</p>}
              <ScrollArea className="h-[60vh] mt-4 border rounded-md">
                <div className="divide-y">
                  {((detail.payload?.pairs ?? []) as ManagerNotificationPair[]).map((p, i) => (
                    <div key={i} className="p-3 text-sm">
                      <div className="text-xs text-muted-foreground mb-1">
                        {p.reservation_a.date} • Tables partagées : {p.shared_table_ids.join(", ")}
                      </div>
                      <div>• {p.reservation_a.start_time.slice(0, 5)} — résa <code className="text-xs">{p.reservation_a.id.slice(0, 8)}</code></div>
                      <div>• {p.reservation_b.start_time.slice(0, 5)} — résa <code className="text-xs">{p.reservation_b.id.slice(0, 8)}</code></div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
